import JustValidate from 'just-validate'
import UAParser from 'ua-parser-js'
import DefaultRenderer from './Default'

/**
 * @typedef {Object} FormFields
 * @property {string} fullName
 * @property {string} address
 * @property {string} city
 * @property {string} state
 * @property {string} zip
 * @property {string} email
 * @property {string} phone
 * @property {string} occupation
 * @property {string} advocacyStatement
 */

/** @type {FormFields} */
const formFields = {
  fullName: 'full-name',
  address: 'address',
  city: 'city',
  state: 'state',
  zip: 'zip',
  email: 'email',
  phone: 'phone',
  occupation: 'occupation',
  advocacyStatement: 'advocacy-statement',
}

/**
 * Prefixes each form field with a given prefix.
 *
 * @param {string} prefix - The prefix to prepend to each form field.
 * @param {Record<string, string>} formFields - The form fields to prefix.
 * @returns {FormFields} An object where the keys are the field names and the values are the prefixed field values.
 */
function prefixFormFields(prefix, formFields) {
  return Object.keys(formFields).reduce((result, key) => {
    result[key] = `${prefix}${formFields[key]}`
    return result
  }, {})
}

const validationPatterns = {
  englishCharacters: /^[a-zA-Z]+$/,
  fullName: {
    englishCharacter: /^[a-zA-Z\s]+$/,
    correctFormat: /^[a-zA-Z]+\s[a-zA-Z]+$/,
  },
  usZipCode: /^\d{5}(?:[-\s]\d{4})?$/,
  usPhoneNumber: /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/,
}

const errorMessageFormatter = {
  requiredField: (field) => `Please enter your ${field}`,
  validField: (field) => `Please enter valid ${field}`,
  englishCharacters: (field) => `${field ?? 'Field'} should contain only letters`,
}

/**
 * Returns a validation rule object for checking if a string contains only English characters.
 * @param {Object} params - An object containing either an errorMessage or a field property, but not both.
 * @note `errorMessage` or `field` should not coexist.
 * @param {string} params.errorMessage - The error message to display if the validation fails.
 * @param {string} params.field - The name of the field to validate.
 * @returns {Object} A validation rule object.
 */
const getEnglishCharactersRule = ({ errorMessage, field } = {}) => ({
  rule: 'customRegexp',
  value: validationPatterns.englishCharacters,
  errorMessage: errorMessage ? errorMessage : errorMessageFormatter.englishCharacters(field),
})

/**
 * @param {Event} event - The form submit event.
 * @param {Record<string, string>} schema - The names of the fields to get the values from.
 * @returns {Record<string, unknown>} An object where the keys are the field names and the values are the field values.
 */
const getFormFieldValues = (event, schema) => {
  const form = event.target
  return Object.keys(schema).reduce((result, key) => {
    const formFieldElement = form.elements[schema[key]]

    //This accounts for field like advocacy-statement that has 2 elements in DOM but only display upon device type
    if (formFieldElement instanceof RadioNodeList) {
      result[key] = Array.from(formFieldElement)
        .map((element) => element.value)
        .find((value) => !!value)
    } else {
      result[key] = formFieldElement.value
    }

    return result
  }, {})
}

const noop = () => {}

class AdvocacyRenderer extends DefaultRenderer {
  /**
   * @private
   * @type {JustValidate}
   */
  #validator
  userAgentData

  constructor(opts) {
    super(opts)
    this.userAgentData = new UAParser(navigator.userAgent).getResult()
  }

  _initializeValidator() {
    this.#validator = new JustValidate('#advocate-form')
  }

  /**
   * @typedef {Object} TrackFormSubmitConfigs
   * @property {VoidFunction} onSuccess
   * @property {(error: Error) => void} onFail
   *
   * @param {FormFields} formFieldValues
   * @param {TrackFormSubmitConfigs} configs
   *
   */
  async trackFormSubmit(formFieldValues, configs = {}) {
    const { onSuccess = noop, onFail = noop } = configs
    try {
      await window.analytics.track('User submitted web form information', {
        ...formFieldValues,
        formName: 'VOC form',
      })
      await super.trackUserInteractionWithElement({
        component: 'button',
        elementName: 'Form submit success - Tusk Advocacy LP',
      })
      onSuccess()
    } catch (e) {
      console.error(e)
      onFail(e)
    }
  }

  isMobile() {
    return this.userAgentData.device.type === 'mobile'
  }

  addFormEventHandlers() {
    this._initializeValidator()
    const advocateFieldQueryId = {
      ...prefixFormFields('#advocate-', formFields),
      advocacyStatement: this.isMobile()
        ? '#advocate-advocacy-statement-mobile'
        : '#advocate-advocacy-statement',
    }

    this.#validator
      .addField(advocateFieldQueryId.fullName, [
        {
          rule: 'required',
          errorMessage: errorMessageFormatter.requiredField('full name'),
        },
        {
          rule: 'customRegexp',
          value: validationPatterns.fullName.englishCharacter,
          errorMessage: errorMessageFormatter.englishCharacters('Full name'),
        },
        {
          rule: 'customRegexp',
          value: validationPatterns.fullName.correctFormat,
          errorMessage: 'Please enter both your first and last names',
        },
      ])
      .addField(advocateFieldQueryId.address, [
        {
          rule: 'required',
          errorMessage: errorMessageFormatter.requiredField('address'),
        },
      ])
      .addField(advocateFieldQueryId.city, [
        {
          rule: 'required',
          errorMessage: errorMessageFormatter.requiredField('city'),
        },
        getEnglishCharactersRule({ field: 'City' }),
      ])
      .addField(advocateFieldQueryId.state, [
        {
          rule: 'required',
          errorMessage: errorMessageFormatter.requiredField('state'),
        },
      ])
      .addField(advocateFieldQueryId.zip, [
        {
          rule: 'required',
          errorMessage: errorMessageFormatter.requiredField('zip code'),
        },
        {
          rule: 'customRegexp',
          value: validationPatterns.usZipCode,
          errorMessage: errorMessageFormatter.validField('zip code'),
        },
      ])
      .addField(advocateFieldQueryId.email, [
        {
          rule: 'required',
          errorMessage: errorMessageFormatter.requiredField('email'),
        },
        {
          rule: 'email',
          errorMessage: errorMessageFormatter.validField('email address'),
        },
      ])
      .addField(advocateFieldQueryId.phone, [
        {
          rule: 'required',
          errorMessage: errorMessageFormatter.requiredField('phone number'),
        },
        {
          rule: 'customRegexp',
          value: validationPatterns.usPhoneNumber,
          errorMessage: errorMessageFormatter.validField('phone number'),
        },
      ])
      .addField(advocateFieldQueryId.occupation, [
        getEnglishCharactersRule({ field: 'Occupation' }),
      ])
      .addField(advocateFieldQueryId.advocacyStatement, [
        {
          rule: 'required',
          errorMessage: errorMessageFormatter.requiredField('advocacy statement'),
        },
      ])
      .onSuccess(async (event) => {
        event.preventDefault()

        const formSchema = {
          ...prefixFormFields('advocate-', formFields),
          advocacyStatement: this.isMobile()
            ? 'advocate-advocacy-statement-mobile'
            : 'advocate-advocacy-statement',
        }
        const fieldValues = getFormFieldValues(event, formSchema)

        const submitButtonSpinner = document.querySelector(
          '[data-js="advocate-form-submit-spinner"]',
        )
        const submitButtonText = document.querySelector(
          '[data-js="advocate-form-submit-button-text"]',
        )

        function startSubmitting() {
          submitButtonText.textContent = 'Submitting...'
          submitButtonSpinner.classList.remove('tw-hidden')
        }

        function endSubmitting() {
          submitButtonText.textContent = 'Submit'
          submitButtonSpinner.classList.add('tw-hidden')
          document
            .querySelector('[data-js="submit-success-helper-text"]')
            .classList.remove('tw-hidden')
          event.target.reset()
        }

        startSubmitting()
        await this.trackFormSubmit(fieldValues, { onSuccess: endSubmitting })
      })
      .onFail(() => {
        super.trackUserInteractionWithElement({
          component: 'button',
          elementName: 'Form submit error - Tusk Advocacy LP',
        })
      })
  }

  onEnterCompleted() {
    super.onEnterCompleted()
    this.addFormEventHandlers()
  }

  onLeaveCompleted() {
    this.#validator.destroy()
  }
}

export default AdvocacyRenderer
